import {
  Body1,
  Body3,
  Button,
  H2,
  TextAndImage,
  type ButtonProps,
} from '@cointracker/styleguide';
import { Grid } from '@cointracker/styleguide/src/Grid';
import { SnapState } from '@cointracker/styleguide/src/SnapPoint/context';
import type { ChildAndClassNameProps } from '@cointracker/styleguide/src/types';
import classNames from 'classnames';
import type { LottieRef } from 'lottie-react';
import { useEffect, useRef, useState } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
import {
  button,
  container,
  content,
  contentInner,
  contentOuter,
  image,
  inactiveLabel,
  labels,
  landingFeature,
  landingFeatureOuter,
  landingFeaturePhysical,
  slidingContentItem,
  subtitle,
  textAndImageInner,
  title,
} from './LandingFeature.css';

export const LandingFeatureContainer = (props: ChildAndClassNameProps) => {
  const { children, className, ...rest } = props;

  // Disable animations while resizing to avoid weird behavior between media queries.
  const [isResizing, setIsResizing] = useState(false);
  const resizeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsResizing(true);

      if (resizeTimer.current) {
        clearTimeout(resizeTimer.current);
      }

      resizeTimer.current = setTimeout(() => {
        setIsResizing(false);
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (resizeTimer.current) {
        clearTimeout(resizeTimer.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      className={classNames(container({ resizing: isResizing }), className)}
      {...rest}
    >
      {children}
    </div>
  );
};

export interface LandingFeatureProps {
  children?: React.ReactNode;
  imgAlt: string;
  imgSrc?: string;
  lottieData?: unknown;
  isLottie?: boolean;
  noFadeIn?: boolean;
  noFadeOut?: boolean;
  sections: string[];
  sectionIndex: number;
  snapState: SnapState;
}

export const LandingFeature = (props: LandingFeatureProps) => {
  const {
    children,
    imgAlt,
    imgSrc,
    isLottie,
    noFadeIn,
    noFadeOut,
    sections,
    sectionIndex,
    snapState,
    lottieData,
    ...rest
  } = props;

  const lottieRef: LottieRef = useRef(null);

  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.5,
  });

  useEffect(() => {
    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = null;

    if (!isIntersecting) {
      timeout.current = setTimeout(() => {
        lottieRef.current?.stop();
        lottieRef.current?.stop();
      }, 1000);
    } else {
      // For some reason, the animation freezes if it plays as soon as it
      // scrolls into view. Waiting for a moment seems to work fine.
      timeout.current = setTimeout(() => {
        lottieRef.current?.play();
      }, 200);
    }
  }, [isIntersecting]);

  return (
    <div className={landingFeaturePhysical}>
      <div className={landingFeatureOuter}>
        <Grid
          className={landingFeature({
            snapState: snapState ?? SnapState.BEFORE,
            noFadeIn,
            noFadeOut,
          })}
          {...rest}
        >
          <TextAndImage
            imgAlt={imgAlt}
            imgSrc={imgSrc}
            imgClassName={image}
            isLottie={isLottie}
            lottieData={lottieData}
            lottieRefCallback={lottieRef}
            ref={ref}
          >
            <div className={textAndImageInner}>
              <div className={labels}>
                {sections.map((section, i) => (
                  <Body3
                    key={i}
                    className={classNames({
                      [inactiveLabel]: sectionIndex !== i,
                    })}
                    caps
                  >
                    {section}
                  </Body3>
                ))}
              </div>
              <div className={contentOuter}>
                <div className={content}>
                  <div className={contentInner}>{children}</div>
                </div>
              </div>
            </div>
          </TextAndImage>
        </Grid>
      </div>
    </div>
  );
};

export const LandingFeatureTitle = (props: ChildAndClassNameProps) => {
  const { children, className, ...rest } = props;

  return (
    <H2 className={classNames(slidingContentItem, title, className)} {...rest}>
      {children}
    </H2>
  );
};

export const LandingFeatureSubtitle = (props: ChildAndClassNameProps) => {
  const { children, className, ...rest } = props;

  return (
    <Body1
      variant="secondary"
      className={classNames(slidingContentItem, subtitle, className)}
      {...rest}
    >
      {children}
    </Body1>
  );
};

export const LandingFeatureButton = (props: ButtonProps) => {
  const { children, className, ...rest } = props;

  const ref = useRef<HTMLButtonElement>(null);

  // Scroll to the physical container when tab navigation is used to play
  // animations correctly
  useEffect(() => {
    const element = ref.current;

    const onFocus = () => {
      const physicalContainer = element?.closest(`.${landingFeaturePhysical}`);
      physicalContainer?.scrollIntoView();
    };
    element?.addEventListener('focus', onFocus);
    return () => {
      element?.removeEventListener('focus', onFocus);
    };
  }, []);

  return (
    <Button
      fluid={false}
      variant="outline"
      hideInitialIcon
      className={classNames(slidingContentItem, button, className)}
      ref={ref}
      {...rest}
    >
      {children}
    </Button>
  );
};
